<script>
   /**************************************************************/
   /* SIMCASettings                                              */
   /* -------------------------------------                      */
   /* shows panel with SIMCA model settings.                     */
   /**************************************************************/

   import { qchisq } from 'mdatools/distributions';
   import { Select, Container, Range } from 'svelte-controls-basic';
   import { genhash } from '@mdatools-apps/shared/utils';
   import { pcamodel_get } from '@mdatools-apps/wasm/pca.js';

   let {
      model,
      className,
      isAlt = false,
      simca = $bindable(),
   } = $props();

   // initialize SIMCA object with default values
   if (!simca && model) {

      const alpha = 5;
      const gamma = 1;
      const limType = 'classic';

      const Nh = pcamodel_get(model, 'cNh');
      const Nq = pcamodel_get(model, 'cNq');
      const Nf = Nh.map((v, i) => v + Nq[i]);

      const ep = 1 - alpha / 100;
      const op = Math.pow(1 - gamma / 100, 1 / model.nrows);
      const eCrit = Nf.map(v => qchisq(ep, v));
      const oCrit = Nf.map(v => qchisq(op, v));

      simca = {
         ncomp: 1,
         alpha,
         gamma,
         limType,
         Nf,
         eCrit,
         oCrit,
         className,
         hash: genhash()
      };
   }

   // prefix for the label ids the controls below are named by. a container only publishes its
   // label to its control when it has both a label and an id, so without this the range and the
   // three selects announce themselves as an unnamed slider and three unnamed radio groups. the
   // component is mounted once per tab and every tab stays in the dom, so it can not be written
   // out by hand
   const uid = $props.id();

   // possible choices for alpha and gamma
   const alphaOptions = [10, 5, 1, 0.1];
   const gammaOptions = [5, 1, 0.1, 0.01];


   // if limType, alpha or gamma changed - recompute Nf and all limits
   $effect(() => {
      const lt = simca.limType.charAt(0);
      const Nh = pcamodel_get(model, lt + 'Nh');
      const Nq = pcamodel_get(model, lt + 'Nq');
      const pe = 1 - simca.alpha / 100;
      const po = Math.pow(1 - simca.gamma / 100, 1 / model.nrows);
      const ncomp = Nh.length;
      const Nf = new Array(ncomp);
      const eCrit = new Array(ncomp);
      const oCrit = new Array(ncomp);
      for (let a = 0; a < ncomp; a++) {
         Nf[a] = Nh[a] + Nq[a];
         eCrit[a] = qchisq(pe, Nf[a]);
         oCrit[a] = qchisq(po, Nf[a]);
      }
      simca.Nf = Nf;
      simca.eCrit = eCrit;
      simca.oCrit = oCrit;
   });

</script>

{#if simca}
<Container name="target-class" label="">
   <p class="title">Target class: {simca.className}</p>
</Container>

<Container name="ncomp" label="# of PCs" id="{uid}-ncomp">
   <Range bind:value={simca.ncomp} min={1} max={model.ncomp} step={1} decNum={0} />
</Container>

<Container name="alpha" label='Alpha, %:' id="{uid}-alpha">
   <Select bind:value={simca.alpha} options={alphaOptions} />
</Container>

{#if !isAlt}
<Container name="gamma" label='Gamma, %:' id="{uid}-gamma">
   <Select bind:value={simca.gamma} options={gammaOptions} />
</Container>
{/if}

<Container name="limits" label='Limits:' id="{uid}-limits">
   <Select bind:value={simca.limType} options={['classic', 'robust']} />
</Container>
{/if}
